import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
} from '@angular/core';

/**
 * Slider de rango de precios con doble control (desde / hasta).
 *
 * Portado de `PriceRangeSliderComponent` del backoffice
 * (`src/components/price-range-slider/`), que ya usaba signals: el cambio es
 * el nombre y que los valores se muestran formateados con su moneda.
 *
 * El padre es el dueño del estado: `desde` y `hasta` son `model()` (two-way).
 * Este componente sólo garantiza el invariante `desde <= hasta` y expone el
 * porcentaje de cada control para pintar el tramo activo; el filtro real lo
 * arma el padre.
 */
@Component({
  selector: 'app-slider-precio',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './slider-precio.html',
  styleUrl: './slider-precio.scss',
})
export class SliderPrecio {
  readonly min = input(0);
  readonly max = input(100_000_000);
  /** 50.000, el mismo `PRECIO_STEP` del backoffice. */
  readonly step = input(50_000);
  /** Deshabilitado: no se arrastra y no se muestran los valores. */
  readonly disabled = input(false);
  /** Símbolo a anteponer a los valores ("U$S" / "$"). Sólo visual. */
  readonly simbolo = input('');
  /**
   * Qué mide el slider, para el nombre accesible de cada control ("Precio
   * desde" / "Superficie desde"). El componente se reusa para superficie, así
   * que dejarlo fijo en "Precio" le mentía al lector de pantalla.
   */
  readonly magnitud = input('Precio');

  /** Extremos seleccionados (two-way con el padre). */
  readonly desde = model(0);
  readonly hasta = model(100_000_000);

  /** Posición porcentual de cada control, para pintar el tramo activo. */
  readonly desdePct = computed(() => this.pct(this.desde()));
  readonly hastaPct = computed(() => this.pct(this.hasta()));

  readonly desdeLabel = computed(() => this.formatear(this.desde()));
  readonly hastaLabel = computed(() => this.formatear(this.hasta()));

  onDesde(evento: Event): void {
    const valor = Number((evento.target as HTMLInputElement).value);
    // El control "desde" nunca puede superar al "hasta".
    this.desde.set(Math.min(valor, this.hasta()));
  }

  onHasta(evento: Event): void {
    const valor = Number((evento.target as HTMLInputElement).value);
    // El control "hasta" nunca puede quedar por debajo del "desde".
    this.hasta.set(Math.max(valor, this.desde()));
  }

  private pct(valor: number): number {
    const span = this.max() - this.min();
    if (span <= 0) return 0;
    // Se acota a 0–100: el padre puede pasar un `desde` fuera de rango si venía
    // de la URL y los extremos cambiaron al cambiar los filtros.
    const bruto = ((valor - this.min()) / span) * 100;
    return Math.min(100, Math.max(0, bruto));
  }

  private formatear(valor: number): string {
    const numero = new Intl.NumberFormat('es-AR', {
      maximumFractionDigits: 0,
    }).format(valor);
    return this.simbolo() ? `${this.simbolo()} ${numero}` : numero;
  }
}
